"use client";
import React from "react";
import { GetAllEventsOutput } from "~/types/EventTypesInferred";
import Link from "next/link";
import UserImage from "~/components/users/UserImage";
import { slugifyUser } from "~/utils/string";
import { cn } from "~/utils/fontsConfig";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../shadcn/Tooltip";

interface AttendeesListProps {
    attendees: GetAllEventsOutput["attendees"];
    showLabel?: boolean;
    showMax?: number;
    iconSize?: number;
    spaceIcons?: boolean;
}

const AttendeesList: React.FC<AttendeesListProps> = ({
    attendees,
    showLabel = true,
    showMax = 5,
    iconSize = 27,
    spaceIcons = true,
}) => {
    if (!attendees) return <></>;
    const withOutLine = spaceIcons
        ? ""
        : "outline outline-2 outline-transparent -outline-offset-1";

    return (
        <div className="flex select-none flex-col gap-3 font-ibm-plex-mono">
            {showLabel && (
                <div className="flex flex-row">
                    <h6>
                        {" "}
                        #
                        {
                            attendees.filter((attendee) => {
                                return attendee.status === "ATTENDING";
                            }).length
                        }{" "}
                        GOING
                    </h6>
                    <h6 className="ml-2">
                        #
                        {
                            attendees.filter((attendee) => {
                                return attendee.status === "MAYBE";
                            }).length
                        }{" "}
                        MAYBE
                    </h6>
                </div>
            )}
            <div
                className={cn(
                    "flex flex-row",
                    spaceIcons ? "gap-2" : "-space-x-1",
                )}
            >
                {attendees
                    .sort(
                        (a, b) =>
                            (!a.user.image ? 1 : -Infinity) -
                            (!b.user.image ? 1 : -Infinity),
                    )
                    .filter((a) => a.status === "ATTENDING")
                    .slice(0, showMax)
                    .map(({ user }, index) => (
                        <div
                            key={index}
                            style={{
                                zIndex: index < 10 ? 20 - index : 10,
                            }}
                            className="relative"
                        >
                            {user.name ? (
                                <TooltipProvider delayDuration={0}>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <Link
                                                className=""
                                                href={`/profile/${slugifyUser(
                                                    user,
                                                )}`}
                                            >
                                                <UserImage
                                                    user={user}
                                                    className={withOutLine}
                                                    iconSize={iconSize}
                                                />
                                            </Link>
                                        </TooltipTrigger>
                                        <TooltipContent sideOffset={-100}>
                                            <p>{user.name ?? ""}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            ) : (
                                <UserImage
                                    user={user}
                                    className={withOutLine}
                                    iconSize={iconSize}
                                />
                            )}
                        </div>
                    ))}
                {attendees.length > showMax && (
                    <div
                        style={{
                            width: iconSize,
                            height: iconSize,
                        }}
                        className={cn(
                            `flex items-center justify-center rounded-full
                            bg-transparent text-[12px] text-white opacity-80`,
                            withOutLine,
                        )}
                    >
                        <p className="ml-[1px]">
                            +
                            {attendees.length - showMax < 100
                                ? attendees.length - showMax
                                : 99}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AttendeesList;
